// src/pages/Community/components/GTASidebar.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./css/GTASidebar.css";

/**
 * GTA 6 Sidebar
 * Props:
 * - active: string
 * - onSelect: (key: string) => void
 * - unreadMap?: { [key: string]: number }
 * - className?: string
 */
export default function GTASidebar({
  active,
  onSelect,
  unreadMap = {},
  className = "",
}) {
  const [isOpen, setIsOpen] = useState(true);

  // Persist collapse state across reloads
  useEffect(() => {
    const saved = localStorage.getItem("gtaSidebarOpen");
    if (saved !== null) setIsOpen(saved === "1");
  }, []);
  useEffect(() => {
    localStorage.setItem("gtaSidebarOpen", isOpen ? "1" : "0");
  }, [isOpen]);

  // Optional: auto-collapse on small screens once on mount
  useEffect(() => {
    const m = window.matchMedia?.("(max-width: 900px)");
    if (m?.matches && localStorage.getItem("gtaSidebarOpen") === null) {
      setIsOpen(false);
    }
  }, []);

  const menu = useMemo(
    () => [
      { key: "general", label: "General Discussion", icon: "🗨️" },
      { key: "missions", label: "Missions", icon: "🎯" },
      { key: "story-characters", label: "Story & Characters", icon: "🎭" },
      { key: "map_locations", label: "Map & Locations", icon: "🗺️" },
      { key: "vehicles_weapons", label: "Vehicles & Weapons", icon: "🚗" },
      // { key: "online_mode", label: "Online Mode", icon: "🌐" },
      // { key: "gameplay_mechanics", label: "Gameplay Mechanics", icon: "🎮" },
      // { key: "tips_tricks", label: "Tips & Tricks", icon: "💡" },
      // { key: "mods_custom", label: "Mods & Custom Content", icon: "🛠" },
      { key: "music_radio", label: "Music & Radio Stations", icon: "🎵" },
       { key: "news_updates", label: "News & Updates", icon: "📰" },
      // { key: "events_dlc", label: "Events & DLC", icon: "🎉" },
      // { key: "leaks_rumors", label: "Leaks & Rumors", icon: "🕵️" },
      {
        key: "achievements",
        label: "Achievements",
        icon: "🏆",
      },
      // { key: "fan_creations", label: "Fan Creations", icon: "🎨" },
      // { key: "screenshots_videos", label: "Screenshots & Videos", icon: "📸" },
      // { key: "bugs_feedback", label: "Bugs & Feedback", icon: "🐞" },
      // {
      //   key: "community_announcements",
      //   label: "Community Announcements",
      //   icon: "📢",
      // },
    ],
    []
  );

  const totalUnread = useMemo(
    () => Object.values(unreadMap).reduce((a, b) => a + (b || 0), 0),
    [unreadMap]
  );

  const handleSelect = useCallback(
    (key) => {
      onSelect?.(key);
      // On small screens, close after navigation for more space
      if (window.matchMedia?.("(max-width: 900px)")?.matches) setIsOpen(false);
      localStorage.setItem("gtaSidebarLast", key);
    },
    [onSelect]
  );

  // Keyboard helpers: Esc to close; [ / ] to move selection
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
      if ((e.key === "[" || e.key === "]") && menu.length) {
        e.preventDefault();
        const idx = Math.max(
          0,
          menu.findIndex((m) => m.key === active)
        );
        const next =
          e.key === "]"
            ? menu[(idx + 1) % menu.length]
            : menu[(idx - 1 + menu.length) % menu.length];
        handleSelect(next.key);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, menu, handleSelect]);

  return (
    <aside
      className={`gta-sidebar ${isOpen ? "open" : "closed"} ${className}`}
      aria-label="Community navigation"
    >
      <div className="gta-sidebar__top">
        <button
          className="gta-sidebar__brand"
          type="button"
          onClick={() => handleSelect("general")}
          title="GTAFanPage"
        />
        <button
          className="gta-sidebar__toggle"
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          aria-label={isOpen ? "Collapse sidebar" : "Expand sidebar"}
          aria-expanded={isOpen}
          title={isOpen ? "Collapse" : "Expand"}
          data-unread={totalUnread > 0 ? String(totalUnread) : undefined}
        >
          {isOpen ? "«" : "»"}
          {totalUnread > 0 && (
            <span className="gta-sidebar__toggle-badge">
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </button>
      </div>

      <nav className="gta-sidebar__menu" role="navigation">
        {menu.map((item) => {
          const isActive = active === item.key;
          const count = unreadMap[item.key] ?? 0;

          return (
            <button
              key={item.key}
              className={`gta-sidebar__item ${isActive ? "active" : ""}`}
              onClick={() => handleSelect(item.key)}
              type="button"
              aria-current={isActive ? "page" : undefined}
              title={!isOpen ? item.label : undefined}
            >
              <span className="gta-sidebar__icon" aria-hidden="true">
                {item.icon}
              </span>

              {/* When collapsed, keep an accessible name for screen readers */}
              {!isOpen && <span className="sr-only">{item.label}</span>}

              {isOpen && (
                <span className="gta-sidebar__label">{item.label}</span>
              )}

              {/* Full badge when open */}
              {isOpen && count > 0 && (
                <span className="gta-sidebar__badge" title={`${count} new`}>
                  {count > 99 ? "99+" : count}
                </span>
              )}

              {/* Minimal dot when closed */}
              {!isOpen && count > 0 && (
                <span className="gta-sidebar__dot" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </nav>

      <div className="gta-sidebar__footer">
        {isOpen && (
          <div className="gta-sidebar__hint">
            <div className="dot live" />
            <span>Live community</span>
          </div>
        )}
      </div>
    </aside>
  );
}
